import { storageServiceModule } from '../../services/storage-service/storage-service';
import { getCookie } from '../../utils/cookie-utils';

export const authInterceptorModule = angular
  .module('Q.interceptors.auth-interceptor', [storageServiceModule])

  .constant('authInterceptorKeys', {
    modes: {
      candidate: {
        loginType: 'candidate',
        storeName: 'authInterceptor.candidateToken',
      },
      embed: {
        // do not use loginType, we always rely on the manually set token instead
        storeName: 'authInterceptor.embedToken', // not used, here just in case
      },
      team: {
        loginType: 'team_member',
        storeName: 'authInterceptor.teamMemberToken',
      },
      admin: {
        loginType: 'admin',
        storeName: 'authInterceptor.adminToken',
      },
    },
    authorizationHeader: 'Authorization',
    loginTypeHeader: 'X-Login-Type',
  })

  /**
   * @docType shared
   * @category provider
   * @description
   * Provides authorization token headers
   * @name Auth-Interceptor
   * @returns {object}
   */
  .provider(
    'authInterceptor',
    function authInterceptorProvider(authInterceptorKeys) {
      let _mode;

      this.setMode = function (mode) {
        if (!authInterceptorKeys.modes[mode]) {
          throw new Error(`authInterceptorProvider: Invalid mode: ${mode}`);
        }
        _mode = mode;
      };

      this.$get = /* @ngInject */ function ($localStorage) {
        let hasOldAuthToken;
        let currentAuthToken;
        if (_mode) {
          _checkMode(_mode);
          // look up localStorage cookie on load, but only on initial load
          // we'll delete this token on a successful response with a cookie
          hasOldAuthToken =
            !hasLoginCookie() && $localStorage[_getModeValues().storeName];
          currentAuthToken =
            hasOldAuthToken && $localStorage[_getModeValues().storeName];
        }

        /**
         * @parent Auth-Interceptor
         * @name _checkMode
         * @private true
         * @description
         * If there is an overridden mode, check if it's valid
         * otherwise check if the authInterceptorProvider was configured with a mode
         * @param mode The overridden mode string (or undefined)
         * @returns {undefined}
         */
        function _checkMode(mode) {
          if (mode && !authInterceptorKeys.modes[mode]) {
            throw new Error(`authInterceptorProvider: Invalid mode: ${mode}`);
          } else if (!authInterceptorKeys.modes[_mode]) {
            throw new Error(
              'authInterceptor: authInterceptorProvider was never configured with a mode!',
            );
          }
        }

        /**
         * @parent Auth-Interceptor
         * @name _getModeValues
         * @private true
         * @description
         * Manually set the authorization token
         * @param mode The overridden mode string (or the pre-configured one)
         * @returns {undefined}
         */
        function _getModeValues(mode = _mode) {
          _checkMode(mode);
          return authInterceptorKeys.modes[mode];
        }

        /**
         * @parent Auth-Interceptor
         * @name setAuthToken
         * @description
         * Manually set the authorization token
         * @param token Value of the token
         * @returns {undefined}
         */
        function setAuthToken(token) {
          if (!token || token === 'null' || token === 'false') token = null;
          // we no longer store auth tokens in localStorage, so always delete it if we're setting the token
          // TODO: remove this eventually
          delete $localStorage[_getModeValues().storeName];
          if (token === null) {
            currentAuthToken = null;
          } else {
            currentAuthToken = token;
          }
        }

        /**
         * @parent Auth-Interceptor
         * @name getAuthToken
         * @description
         * Returns the current auth token
         * @returns {*}
         */
        function getAuthToken() {
          return currentAuthToken || null;
        }

        /**
         * @parent Auth-Interceptor
         * @name clearAuthToken
         * @description
         * Clears the current authorization token, but does not log the current user out
         * @returns {*}
         */
        function clearAuthToken() {
          setAuthToken(null);
        }

        /**
         * @parent Auth-Interceptor
         * @name hasAuthToken
         * @description
         * Returns true if the current user has an authorization token.
         * Does not guarantee that the current token is valid.
         * @returns {boolean} true if there is a token, false otherwise
         */
        function hasAuthToken() {
          return !!getAuthToken();
        }

        /**
         * @parent Auth-Interceptor
         * @name setRequestAuthToken
         * @description
         * Sets the Authorization header during any $http request, if there is a current
         * authorization token.
         * @param config $http config
         * @returns {*} possibly modified $http config.
         */
        function setRequestAuthToken(config) {
          if (_authDisabled(config)) {
            return config;
          }

          config.headers = config.headers || {};

          if (hasOldAuthToken || !getAuthToken()) {
            // always set the login type header if we have a cookie or are being asked to do so
            config.headers[authInterceptorKeys.loginTypeHeader] =
              _getModeValues().loginType;
          }

          if (getAuthToken()) {
            config.headers[authInterceptorKeys.authorizationHeader] =
              getAuthToken();
          }
          return config;
        }

        /**
         * @parent Auth-Interceptor
         * @name _authDisabled
         * @description
         * Checks to see if the http config disabled authorization
         * @param config $http config
         * @returns {boolean} whether auth is disabled
         */
        function _authDisabled(config) {
          return 'authorization' in config && !config.authorization;
        }

        /**
         * Returns true if we can find a logged in cookie or localStorage value
         * @returns {boolean}
         */
        function loggedIn() {
          return hasLoginCookie() || hasAuthToken();
        }

        /**
         * Returns true if the correct login cookie is set
         * @returns {boolean}
         */
        function hasLoginCookie() {
          return getCookie(_getModeValues().loginType + '_login') === 'true';
        }

        return {
          loggedIn,
          setAuthToken,
          getAuthToken,
          clearAuthToken,
          request: setRequestAuthToken,
          // TODO: remove this whole method eventually
          response(response) {
            // clear auth token if we get a cookie
            if (hasLoginCookie() && $localStorage[_getModeValues().storeName]) {
              // we no longer store auth tokens in localStorage, so always delete it if we're setting the token
              delete $localStorage[_getModeValues().storeName];
              // also revert the auth token
              currentAuthToken = null;
              hasOldAuthToken = false;
            }
            return response;
          },
        };
      };
    },
  )

  .config(function ($httpProvider) {
    $httpProvider.interceptors.push('authInterceptor');
  });
